// CPC — wallet connect in explorer header (DGC Wallet extension only)
import { useCurrentAccount, useSuiClientQuery } from "@mysten/dapp-kit";
import { Text } from "@mysten/ui";
import { Link } from "react-router-dom";

import { CPC_SYMBOL, DGC_TYPE_ARG } from "~/utils/cpcCoin";
import { tryFormatCpcAddress } from "~/utils/cpcAddress";
import { formatMistAsDgc, shortenAddress } from "~/utils/explorerFormat";
import { useT } from "~/i18n/LocaleContext";

function formatMist(mist: string | undefined): string {
  return formatMistAsDgc(mist, { maxDecimals: 4 });
}

export function CpcWalletBar({ variant = 'default' }: { variant?: 'default' | 'sidebar' }) {
  const t = useT();
  const account = useCurrentAccount();
  const { data } = useSuiClientQuery(
    "getBalance",
    {
      owner: account?.address ?? "",
      coinType: DGC_TYPE_ARG,
    },
    { enabled: !!account?.address },
  );

  const displayAddr = account?.address ? tryFormatCpcAddress(account.address) : "";
  const shortAddr = account?.address ? shortenAddress(account.address) : "";

  if (variant === 'sidebar') {
    if (!account) {
      return (
        <div className="rounded-lg border border-dashed border-white/10 bg-[#0F172A]/50 px-3 py-2.5">
          <p className="text-xs text-[#94a3b8]">{t('wallet.viewBalance')}</p>
        </div>
      );
    }

    const balance = formatMistAsDgc(
      (data as { fundsInAddressBalance?: string } | undefined)?.fundsInAddressBalance ?? data?.totalBalance,
      { maxDecimals: 4 },
    );

    return (
      <Link
        to={`/address/${account.address}`}
        title={displayAddr}
        className="block rounded-lg border border-white/10 bg-[#0F172A]/80 px-3 py-2.5 transition-colors hover:border-[#38BDF8]/30 hover:bg-[#0F172A]"
      >
        <p className="text-[10px] font-medium uppercase tracking-wide text-[#94a3b8]">{t('common.available')}</p>
        <p className="mt-0.5 text-base font-bold tabular-nums text-[#38BDF8]">
          {balance} {CPC_SYMBOL}
        </p>
        <p className="mt-1 truncate font-mono text-[11px] text-[#aeb9d0]">
          {shortAddr}
        </p>
      </Link>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      {account && data && (
        <Link
          to={`/address/${account.address}`}
          className="hidden max-w-[140px] truncate rounded-md bg-steel-50 px-2 py-1 sm:block"
          title={displayAddr}
        >
          <Text variant="caption/medium" className="text-[#aeb9d0]">
            {formatMist(data.fundsInAddressBalance)} {CPC_SYMBOL}
          </Text>
          <Text
            variant="caption/medium"
            title={displayAddr}
            className="mt-1 text-[#38BDF8]"
          >
            {shortAddr}
          </Text>
        </Link>
      )}
    </div>
  );
}
